import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiArrowLeft, FiEdit2, FiMail, FiPhone, FiMapPin, FiCamera, FiSave, FiX,
  FiLogOut, FiSettings, FiShield, FiBell, FiHelpCircle, FiChevronRight,
  FiCheck
} from "react-icons/fi";
import { FaCrown } from "react-icons/fa";

/* ------------------------------------------------------------------ */
/*  Loads the display / body / mono typefaces once, app-wide.          */
/* ------------------------------------------------------------------ */
function useGoyeeFonts() {
  useEffect(() => {
    const id = "goyee-profile-fonts";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@500;600;700&display=swap";
    document.head.appendChild(link);
  }, []);
}

export default function Profile() {
  const navigate = useNavigate();
  useGoyeeFonts();

  // Edit State
  const [isEditing, setIsEditing] = useState(false);
  const [userName, setUserName] = useState(localStorage.getItem("username") || "User");
  const [email, setEmail] = useState(localStorage.getItem("email") || "No email provided");
  const [phone, setPhone] = useState(localStorage.getItem("phone") || "No phone provided");
  const [location, setLocation] = useState(localStorage.getItem("location") || "No location provided");

  const [securityLock, setSecurityLock] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to log out?")) {
      localStorage.removeItem("isLoggedIn");
      localStorage.removeItem("username");
      window.dispatchEvent(new Event("loginStatusChanged"));
      navigate("/");
    }
  };

  const handleSaveProfile = () => {
    localStorage.setItem("username", userName);
    window.dispatchEvent(new Event("loginStatusChanged"));
    setIsEditing(false);
    alert("Profile updated successfully!");
  };

  return (
    <div className="min-h-screen bg-[#07090A] text-[#F5F7F6] font-['Inter'] relative overflow-hidden px-5 py-10 sm:px-8 lg:px-14 lg:py-14">

      <style>{`
        @keyframes goyee-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes goyee-pulse-ring { 0%, 100% { opacity: .55; transform: scale(1); } 50% { opacity: 1; transform: scale(1.06); } }
        @keyframes goyee-float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-14px); } }
        @keyframes goyee-rise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        .goyee-rise { animation: goyee-rise .6s cubic-bezier(.16,1,.3,1) both; }
      `}</style>

      {/* Ambient mesh background */}
      <div aria-hidden className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-32 w-[560px] h-[560px] rounded-full bg-[#1CE0A0] opacity-[0.14] blur-[140px]" style={{ animation: "goyee-float 10s ease-in-out infinite" }} />
        <div className="absolute -bottom-52 -right-32 w-[620px] h-[620px] rounded-full bg-[#0E4B3E] opacity-30 blur-[150px]" style={{ animation: "goyee-float 12s ease-in-out infinite 1s" }} />
        <div className="absolute top-1/3 right-1/4 w-[380px] h-[380px] rounded-full bg-[#F0B858] opacity-[0.06] blur-[130px]" />
        <div className="absolute inset-0 opacity-[0.035]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "56px 56px" }} />
      </div>

      <div className="max-w-6xl mx-auto relative z-10">

        {/* Top bar */}
        <div className="flex items-center justify-between mb-10 goyee-rise">
          <button onClick={() => navigate(-1)} className="flex items-center gap-3 group">
            <div className="w-11 h-11 rounded-full bg-white/[0.04] border border-white/[0.08] backdrop-blur-xl flex items-center justify-center group-hover:bg-white/[0.08] group-hover:border-[#1CE0A0]/40 transition-all">
              <FiArrowLeft size={18} className="text-[#C9D4CF] group-hover:text-[#1CE0A0] group-hover:-translate-x-0.5 transition-all" />
            </div>
            <span className="text-sm font-medium text-[#8FA39C] group-hover:text-[#E9EFEC] transition-colors hidden sm:inline">Back</span>
          </button>

          <div className="flex items-center gap-2 bg-gradient-to-r from-[#F0B858]/15 to-[#F0B858]/5 border border-[#F0B858]/30 text-[#F0D9A0] px-4 py-2 rounded-full backdrop-blur-xl">
            <FaCrown size={13} className="text-[#F0B858]" />
            <span className="text-xs font-semibold tracking-wide">Client Account</span>
          </div>
        </div>

        {/* Page heading */}
        <div className="mb-8 goyee-rise" style={{ animationDelay: "40ms" }}>
          <p className="text-[11px] font-semibold tracking-[0.28em] text-[#1CE0A0] uppercase font-['JetBrains_Mono'] mb-2">Account</p>
          <h1 className="text-4xl sm:text-5xl font-['Sora'] font-bold text-[#F5F7F6] tracking-tight">Your Profile</h1>
          <p className="text-[#8FA39C] mt-2 text-[15px]">Manage your identity and preferences in one place.</p>
        </div>

        {/* Hero identity strip */}
        <div className="relative rounded-[28px] p-[1px] bg-gradient-to-br from-white/[0.14] via-white/[0.04] to-transparent mb-6 goyee-rise" style={{ animationDelay: "80ms" }}>
          <div className="rounded-[27px] bg-gradient-to-br from-white/[0.05] to-white/[0.015] backdrop-blur-2xl px-6 py-8 sm:px-10 sm:py-10">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-7">

              {/* Avatar with gradient ring */}
              <div className="relative shrink-0">
                <div className="w-28 h-28 rounded-[26px] p-[3px] bg-gradient-to-br from-[#1CE0A0] via-[#0E4B3E] to-[#F0B858]">
                  <div className="w-full h-full rounded-[23px] bg-[#0B0F0D] flex items-center justify-center">
                    <span className="text-4xl font-['Sora'] font-bold bg-gradient-to-br from-[#1CE0A0] to-[#8FE8C8] bg-clip-text text-transparent">
                      {userName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                </div>
                {!isEditing ? (
                  <div className="absolute -bottom-1.5 -right-1.5 w-7 h-7 bg-[#1CE0A0] rounded-full flex items-center justify-center border-[3px] border-[#07090A]" title="Verified account">
                    <FiCheck size={13} className="text-[#052018]" strokeWidth={3} />
                  </div>
                ) : (
                  <button className="absolute -bottom-2 -right-2 w-10 h-10 bg-[#1CE0A0] text-[#052018] rounded-2xl flex items-center justify-center shadow-[0_8px_24px_rgba(28,224,160,0.35)] hover:scale-110 transition-transform">
                    <FiCamera size={17} />
                  </button>
                )}
              </div>

              {/* Name / handle / edit */}
              <div className="flex-1 w-full text-center sm:text-left">
                {!isEditing ? (
                  <>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <h2 className="text-2xl sm:text-3xl font-['Sora'] font-bold text-[#F5F7F6] tracking-tight">{userName}</h2>
                        <p className="text-[#1CE0A0] font-medium mt-1 text-sm font-['JetBrains_Mono']">@goyee_trial</p>
                      </div>
                      <button
                        onClick={() => setIsEditing(true)}
                        className="inline-flex items-center justify-center gap-2 bg-white/[0.06] border border-white/10 text-[#E9EFEC] px-6 py-3 rounded-2xl font-semibold text-sm hover:bg-[#1CE0A0]/10 hover:border-[#1CE0A0]/40 hover:text-[#1CE0A0] transition-all"
                      >
                        <FiEdit2 size={15} /> Edit Profile
                      </button>
                    </div>
                    <div className="flex flex-wrap justify-center sm:justify-start gap-2 mt-5">
                      <span className="text-xs font-medium text-[#8FA39C] bg-white/[0.04] border border-white/[0.06] rounded-full px-3 py-1.5">Messaging tier · Standard</span>
                      <span className="text-xs font-medium text-[#8FA39C] bg-white/[0.04] border border-white/[0.06] rounded-full px-3 py-1.5">Account verified</span>
                    </div>
                  </>
                ) : (
                  <div className="w-full space-y-4">
                    <input
                      type="text"
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      className="w-full px-5 py-4 bg-white/[0.04] border-2 border-[#1CE0A0]/30 rounded-2xl focus:outline-none focus:border-[#1CE0A0] font-['Sora'] font-semibold text-center sm:text-left text-xl text-[#F5F7F6] transition-colors"
                    />
                    <div className="flex gap-3">
                      <button onClick={handleSaveProfile} className="flex-1 flex justify-center items-center gap-2 bg-[#1CE0A0] text-[#052018] py-3.5 rounded-2xl font-bold hover:shadow-[0_10px_28px_rgba(28,224,160,0.35)] transition-all">
                        <FiSave size={17} /> Save
                      </button>
                      <button onClick={() => setIsEditing(false)} className="flex-1 flex justify-center items-center gap-2 bg-white/[0.05] border border-white/10 text-[#C9D4CF] py-3.5 rounded-2xl font-bold hover:bg-white/[0.09] transition-all">
                        <FiX size={17} /> Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Contact rows */}
            <div className="mt-9 pt-8 border-t border-white/[0.06] grid grid-cols-1 sm:grid-cols-3 gap-6">
              <DetailItem icon={FiMail} title="Email" value={email} isEditing={isEditing} onChange={setEmail} />
              <DetailItem icon={FiPhone} title="Phone" value={phone} isEditing={isEditing} onChange={setPhone} />
              <DetailItem icon={FiMapPin} title="Location" value={location} isEditing={isEditing} onChange={setLocation} />
            </div>
          </div>
        </div>

        {/* Preferences + Logout */}
        <div className="grid grid-cols-1 gap-6">
          <div className="goyee-rise rounded-[28px] p-[1px] bg-gradient-to-br from-white/[0.14] via-white/[0.04] to-transparent" style={{ animationDelay: "120ms" }}>
            <div className="rounded-[27px] bg-gradient-to-br from-white/[0.05] to-white/[0.015] backdrop-blur-2xl overflow-hidden">
              <div className="px-7 py-6 border-b border-white/[0.06] flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-white/[0.05] border border-white/[0.08] flex items-center justify-center text-[#C9D4CF]">
                  <FiSettings size={19} />
                </div>
                <div>
                  <h3 className="text-lg font-['Sora'] font-bold text-[#F5F7F6]">Preferences</h3>
                  <p className="text-xs text-[#8FA39C] mt-0.5">Security, alerts, and support</p>
                </div>
              </div>
              <div className="p-3.5 space-y-3">
                {/* Security & Privacy Card */}
                <div className="w-full flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-[#1CE0A0]/30 transition-all gap-3">
                  <div className="flex items-center gap-4 text-[#E9EFEC] font-semibold text-[15px]">
                    <div className="w-9 h-9 rounded-xl bg-white/[0.04] border border-white/[0.06] flex items-center justify-center text-[#1CE0A0] shrink-0">
                      <FiShield size={16} />
                    </div>
                    <div className="text-left">
                      <p className="font-bold text-white text-sm">Security & Privacy</p>
                      <p className="text-[11px] text-[#8FA39C] font-normal">Active session encryption</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 self-end sm:self-center">
                    <span className="text-[10px] font-bold text-[#1CE0A0] bg-[#1CE0A0]/10 border border-[#1CE0A0]/20 px-2.5 py-1 rounded-full uppercase tracking-wider">
                      🟢 Encrypted
                    </span>
                    <button 
                      type="button"
                      onClick={() => setSecurityLock(!securityLock)}
                      className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors cursor-pointer ${securityLock ? 'bg-[#1CE0A0]' : 'bg-white/10'}`}
                    >
                      <div className={`w-4 h-4 rounded-full bg-[#052018] shadow-md transform transition-transform ${securityLock ? 'translate-x-5' : 'translate-x-0'}`} />
                    </button>
                  </div>
                </div>

                {/* Notifications Card */}
                <div className="w-full flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-[#1CE0A0]/30 transition-all gap-3">
                  <div className="flex items-center gap-4 text-[#E9EFEC] font-semibold text-[15px]">
                    <div className="w-9 h-9 rounded-xl bg-white/[0.04] border border-white/[0.06] flex items-center justify-center text-[#1CE0A0] shrink-0">
                      <FiBell size={16} />
                    </div>
                    <div className="text-left">
                      <p className="font-bold text-white text-sm">Notifications</p>
                      <p className="text-[11px] text-[#8FA39C] font-normal">Sound & alert popups</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 self-end sm:self-center">
                    <span className="text-[10px] font-bold text-white/50 bg-white/5 border border-white/10 px-2.5 py-1 rounded-full uppercase tracking-wider">
                      Sound Alerts
                    </span>
                    <button 
                      type="button"
                      onClick={() => setNotificationsEnabled(!notificationsEnabled)}
                      className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors cursor-pointer ${notificationsEnabled ? 'bg-[#1CE0A0]' : 'bg-white/10'}`}
                    >
                      <div className={`w-4 h-4 rounded-full bg-[#052018] shadow-md transform transition-transform ${notificationsEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                    </button>
                  </div>
                </div>

                {/* Help & Support Card */}
                <div className="w-full flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-[#1CE0A0]/30 transition-all gap-3">
                  <div className="flex items-center gap-4 text-[#E9EFEC] font-semibold text-[15px]">
                    <div className="w-9 h-9 rounded-xl bg-white/[0.04] border border-white/[0.06] flex items-center justify-center text-[#1CE0A0] shrink-0">
                      <FiHelpCircle size={16} />
                    </div>
                    <div className="text-left">
                      <p className="font-bold text-white text-sm">Help & Support</p>
                      <p className="text-[11px] text-[#8FA39C] font-normal">Report issue or feedback</p>
                    </div>
                  </div>
                  <button 
                    type="button"
                    onClick={() => window.dispatchEvent(new CustomEvent('openSupportModal'))}
                    className="self-end sm:self-center px-4 py-2 bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white font-extrabold text-xs rounded-xl shadow-[0_4px_14px_rgba(37,211,102,0.3)] hover:scale-105 transition-transform flex items-center gap-1.5 cursor-pointer"
                  >
                    💬 Get Support
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="goyee-rise w-full flex justify-between items-center bg-gradient-to-br from-white/[0.05] to-white/[0.015] backdrop-blur-2xl p-6 rounded-[27px] border border-white/[0.08] hover:border-[#FF5C6C]/40 hover:shadow-[0_15px_40px_rgba(255,92,108,0.12)] transition-all group"
            style={{ animationDelay: "160ms" }}
          >
            <div className="flex items-center gap-5 text-[#FF8993] font-bold text-lg">
              <div className="w-12 h-12 bg-[#FF5C6C]/10 border border-[#FF5C6C]/20 rounded-2xl flex items-center justify-center group-hover:bg-[#FF5C6C] group-hover:text-white transition-colors">
                <FiLogOut size={20} />
              </div>
              Log out securely
            </div>
            <FiChevronRight size={22} className="text-[#5C6D67] group-hover:text-[#FF5C6C] group-hover:translate-x-1 transition-all" />
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailItem({ icon: Icon, title, value, isEditing, onChange }) {
  return (
    <div className="flex items-center gap-4">
      <div className="w-11 h-11 shrink-0 rounded-2xl bg-white/[0.05] border border-white/[0.08] flex items-center justify-center text-[#1CE0A0]">
        <Icon size={17} />
      </div>
      <div className="flex-1 text-left min-w-0">
        <p className="text-[10px] font-semibold text-[#8FA39C] uppercase tracking-[0.2em] font-['JetBrains_Mono'] mb-1">{title}</p>
        {!isEditing ? (
          <p className="text-[15px] font-semibold text-[#E9EFEC] truncate">{value}</p>
        ) : (
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 font-medium text-sm text-[#F5F7F6] focus:outline-none focus:border-[#1CE0A0]/50 transition-colors"
          />
        )}
      </div>
    </div>
  );
}